import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar, Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, InputBase,
  LinearProgress, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography,
} from '@mui/material';
import { Close, Delete, Send, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Post, photoUrl } from '../services/postService';
import { STORY_REACTIONS, StoryViewer as StoryViewerRow, storyService } from '../services/storyService';
import { isVideoKey } from '../utils/media';
import PostVideo from './PostVideo';
import { useNotification } from './NotificationContext';
import { formatDistanceToNow } from 'date-fns';

export interface StoryGroup {
  authorUserId: string;
  authorDisplayName: string;
  authorProfilePhotoUrl?: string | null;
  stories: Post[];
}

interface StoryViewerProps {
  groups: StoryGroup[];
  startGroup: number;
  currentUserId: string;
  /** View counts for the current user's own stories, by story id. */
  viewCounts: Record<string, number>;
  onClose: () => void;
  onDeleted: (storyId: string) => void;
  /** Fired once per story the viewer actually watched (not their own). */
  onSeen: (storyId: string) => void;
}

const PHOTO_MS = 5000;

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function timeLeft(expiresAt?: string | null): string {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expiring';
  const h = Math.floor(ms / 3_600_000);
  return h >= 1 ? `${h}h left` : `${Math.max(1, Math.floor(ms / 60_000))}m left`;
}

function timeAgo(iso: string) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ''; }
}

/** Author-only sheet: who watched this story and what they tapped. */
const SeenBySheet: React.FC<{ storyId: string; onClose: () => void }> = ({ storyId, onClose }) => {
  const navigate = useNavigate();
  const [viewers, setViewers] = useState<StoryViewerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    storyService.viewers(storyId).then(setViewers).catch(() => setError('Could not load viewers'));
  }, [storyId]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Typography component="span" variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
          {viewers ? `Seen by ${viewers.length}` : 'Seen by'}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {!viewers && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>}
        {error && <Typography color="error" variant="body2">{error}</Typography>}
        {viewers && viewers.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No one has watched this yet.
          </Typography>
        )}
        {viewers && viewers.length > 0 && (
          <List disablePadding>
            {viewers.map(v => (
              <ListItem
                key={v.userId}
                disableGutters
                onClick={() => navigate(`/u/${v.userId}`)}
                sx={{ cursor: 'pointer', borderRadius: 1, px: 1, '&:hover': { bgcolor: 'action.hover' } }}
                secondaryAction={v.reaction ? <Typography sx={{ fontSize: 22 }} aria-label={`Reacted ${v.reaction}`}>{v.reaction}</Typography> : undefined}
              >
                <ListItemAvatar>
                  <Avatar src={v.profilePhotoUrl ?? undefined} sx={{ bgcolor: 'primary.main' }}>
                    {!v.profilePhotoUrl && (initials(v.displayName) || '?')}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={v.displayName} secondary={timeAgo(v.viewedAt)} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Full-screen story player: tap/click the right or left side (or use the arrow keys) to move
 * between stories, photos advance on a 5-second timer, videos advance when they finish.
 * Someone else's story has a reply bar and one-tap reactions that land in your DM thread with
 * the author; your own shows who has seen it. The clock pauses while you type or read the list.
 */
const StoryViewer: React.FC<StoryViewerProps> = ({ groups, startGroup, currentUserId, viewCounts, onClose, onDeleted, onSeen }) => {
  const { showError, showSuccess } = useNotification();
  const [groupIndex, setGroupIndex] = useState(startGroup);
  const [storyIndex, setStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [composing, setComposing] = useState(false);
  const [seenBySheet, setSeenBySheet] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const recorded = useRef(new Set<string>());

  const group = groups[groupIndex];
  const story = group?.stories[storyIndex];
  const isVideo = !!story && isVideoKey(story.photoKeys[0] ?? '');
  const isOwn = !!group && group.authorUserId === currentUserId;
  const paused = composing || seenBySheet || sending;

  const next = useCallback(() => {
    if (!group) return onClose();
    if (storyIndex < group.stories.length - 1) {
      setStoryIndex(i => i + 1);
    } else if (groupIndex < groups.length - 1) {
      setGroupIndex(i => i + 1);
      setStoryIndex(0);
    } else {
      onClose();
    }
  }, [group, groupIndex, groups.length, storyIndex, onClose]);

  const prev = useCallback(() => {
    if (storyIndex > 0) {
      setStoryIndex(i => i - 1);
    } else if (groupIndex > 0) {
      const previous = groups[groupIndex - 1];
      setGroupIndex(i => i - 1);
      setStoryIndex(previous.stories.length - 1);
    }
  }, [groupIndex, groups, storyIndex]);

  // Record the view and restart the clock whenever the visible story changes.
  useEffect(() => {
    if (!story) return;
    setProgress(0);
    setDraft('');
    if (story.authorUserId !== currentUserId && !recorded.current.has(story.postId)) {
      recorded.current.add(story.postId);
      onSeen(story.postId);
      storyService.recordView(story.postId).catch(() => { /* seen-state is best effort */ });
    }
  }, [story, currentUserId, onSeen]);

  // Photo timer. (Videos report their own progress and end.) Accumulates across pauses so a
  // reply doesn't restart the picture from zero.
  const elapsedRef = useRef(0);
  useEffect(() => { elapsedRef.current = 0; }, [story]);
  useEffect(() => {
    if (!story || isVideo || paused) return;
    const startedAt = Date.now() - elapsedRef.current;
    const id = window.setInterval(() => {
      elapsedRef.current = Date.now() - startedAt;
      const pct = (elapsedRef.current / PHOTO_MS) * 100;
      if (pct >= 100) { window.clearInterval(id); next(); } else { setProgress(pct); }
    }, 50);
    return () => window.clearInterval(id);
  }, [story, isVideo, paused, next]);

  // Pause/resume a video alongside the photo clock.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause(); else v.play().catch(() => { /* autoplay rules */ });
  }, [paused]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (composing) { if (e.key === 'Escape') (document.activeElement as HTMLElement | null)?.blur(); return; }
      if (seenBySheet) return;
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, onClose, composing, seenBySheet]);

  if (!group || !story) return null;

  const handleDelete = async () => {
    try {
      await storyService.remove(story.postId);
      onDeleted(story.postId);
      // The list shrinks under us; closing is the simplest correct behavior.
      onClose();
    } catch {
      showError('Failed to delete story');
    }
  };

  const send = async (request: { text?: string; reaction?: string }) => {
    if (sending) return;
    setSending(true);
    try {
      await storyService.reply(story.postId, request);
      setDraft('');
      showSuccess(request.reaction && !request.text ? `Sent ${request.reaction} to ${group.authorDisplayName}` : `Reply sent to ${group.authorDisplayName}`);
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const viewCount = viewCounts[story.postId] ?? 0;

  return (
    <Dialog open fullScreen onClose={onClose} PaperProps={{ sx: { bgcolor: 'common.black' } }}>
      <Box sx={{ position: 'relative', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 480, height: '100%', maxHeight: '100dvh', bgcolor: 'common.black' }}>
          {isVideo ? (
            <PostVideo
              key={story.postId}
              ref={videoRef}
              videoKey={story.photoKeys[0]}
              autoPlay
              playsInline
              onEnded={next}
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (v.duration) setProgress((v.currentTime / v.duration) * 100);
              }}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <img src={photoUrl(story.photoKeys[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          )}

          {/* Tap zones — stop above the reply bar so it stays tappable */}
          <Box onClick={prev} sx={{ position: 'absolute', top: 72, bottom: 96, left: 0, width: '30%', cursor: 'pointer' }} />
          <Box onClick={next} sx={{ position: 'absolute', top: 72, bottom: 96, right: 0, width: '70%', cursor: 'pointer' }} />

          {/* Top bar: progress segments + author */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, p: 1.5, background: 'linear-gradient(rgba(0,0,0,0.6), transparent)' }}>
            <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
              {group.stories.map((s, i) => (
                <LinearProgress
                  key={s.postId}
                  variant="determinate"
                  value={i < storyIndex ? 100 : i === storyIndex ? progress : 0}
                  sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: '#fff', transition: 'none' } }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar src={group.authorProfilePhotoUrl ?? undefined} sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
                {!group.authorProfilePhotoUrl && (initials(group.authorDisplayName) || '?')}
              </Avatar>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, flexGrow: 1 }}>
                {isOwn ? 'Your story' : group.authorDisplayName}
                <Typography component="span" variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}>
                  {timeLeft(story.expiresAt)}
                </Typography>
              </Typography>
              {isOwn && (
                <IconButton aria-label="Delete story" onClick={handleDelete} sx={{ color: '#fff' }}><Delete /></IconButton>
              )}
              <IconButton aria-label="Close" onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
            </Stack>
          </Box>

          {/* Bottom: caption, then either the reply bar (others' stories) or Seen by (your own) */}
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 2, pb: 'max(16px, env(safe-area-inset-bottom))', background: 'linear-gradient(transparent, rgba(0,0,0,0.75))' }}>
            {story.text && (
              <Typography variant="body1" sx={{ color: '#fff', textAlign: 'center', overflowWrap: 'anywhere', mb: 1.5 }}>{story.text}</Typography>
            )}

            {isOwn ? (
              <Stack direction="row" justifyContent="center">
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={`Seen by ${viewCount}`}
                  onClick={() => setSeenBySheet(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSeenBySheet(true); }}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: '#fff', px: 1.5, py: 0.75, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.15)', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
                >
                  <Visibility fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {viewCount === 0 ? 'No views yet' : `Seen by ${viewCount}`}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ flexWrap: 'wrap' }}>
                  {STORY_REACTIONS.map(emoji => (
                    <IconButton
                      key={emoji}
                      aria-label={`React ${emoji}`}
                      disabled={sending}
                      onClick={() => send({ reaction: emoji })}
                      sx={{ fontSize: 24, width: 44, height: 44, color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', transform: 'scale(1.15)' }, transition: 'transform 120ms' }}
                    >
                      {emoji}
                    </IconButton>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 6, pl: 2, pr: 0.5, py: 0.25, border: '1px solid rgba(255,255,255,0.35)' }}>
                  <InputBase
                    fullWidth
                    placeholder={`Reply to ${group.authorDisplayName.split(' ')[0]}…`}
                    value={draft}
                    inputProps={{ maxLength: 500, 'aria-label': `Reply to ${group.authorDisplayName}` }}
                    onChange={(e) => setDraft(e.target.value)}
                    onFocus={() => setComposing(true)}
                    onBlur={() => setComposing(false)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) { e.preventDefault(); send({ text: draft.trim() }); } }}
                    sx={{ color: '#fff', fontSize: '0.95rem', '& input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }}
                  />
                  <IconButton aria-label="Send reply" onClick={() => draft.trim() && send({ text: draft.trim() })} disabled={!draft.trim() || sending} sx={{ color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' } }}>
                    {sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Send fontSize="small" />}
                  </IconButton>
                </Stack>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      {seenBySheet && <SeenBySheet storyId={story.postId} onClose={() => setSeenBySheet(false)} />}
    </Dialog>
  );
};

export default StoryViewer;
